import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, signal, ViewChild, OnInit, AfterViewInit, inject, ElementRef } from '@angular/core';
import {
  AbstractControl,
  ReactiveFormsModule,
  FormGroup,
  FormBuilder,
  ValidationErrors,
  Validators,
  FormsModule,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { SharedService } from '@services/shared.service';
import { CountryService } from '@services/country.service';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { AuthService } from 'src/app/Auth/AuthService';
import { NgxIntlTelInputModule } from 'ngx-intl-tel-input';
import {
  CountryISO,
  SearchCountryField,
  NgxIntlTelInputComponent,
} from 'ngx-intl-tel-input';
import { PagesService } from '@services/pages.service';
import { TranslateModule } from '@ngx-translate/core';
import { environment } from '@env';
import { OnboardingDraftService } from '../onboarding-draft.service';

// Letters in any script (Arabic included), with inner spaces, hyphens, apostrophes and dots —
// minLength(2) alone let "   ", "1234" and "<script>" through as names.
const PERSON_NAME_PATTERN = /^\s*\p{L}[\p{L}\p{M}\s'.-]*\p{L}\.?\s*$/u;

/**
 * A first or last name, judged on what is left after trimming. Validators.required and
 * minLength count spaces, so "   " passed both and only failed the letters pattern — the
 * field said "Use letters only" instead of "required". One error at a time, in that order.
 */
export function personNameValidator(control: AbstractControl): ValidationErrors | null {
  const value = String(control.value ?? '').trim();
  if (!value) return { required: true };
  if (value.length < 2) return { minlength: { requiredLength: 2, actualLength: value.length } };
  return PERSON_NAME_PATTERN.test(value) ? null : { pattern: { requiredPattern: String(PERSON_NAME_PATTERN), actualValue: value } };
}

/** An email judged without its leading and trailing spaces — " jane@example.com " is a valid address. */
export function trimmedEmailValidator(control: AbstractControl): ValidationErrors | null {
  const value = String(control.value ?? '').trim();
  if (!value) return { required: true };
  return Validators.email({ value } as AbstractControl);
}

// Text fields whose surrounding spaces are dropped. The password is left as typed on purpose.
const TRIMMED_FIELDS = ['fname', 'lname', 'email'];

// Characters refused in the phone number field.
const PHONE_BLOCKED_CHARS = /^[+-]$/;

/** Removes "+" and "-" from a phone input, keeping the caret where the user was typing. */
export function stripPhoneSigns(input: HTMLInputElement): void {
  const value = input.value;
  const cleaned = value.replace(/[+-]/g, '');
  if (cleaned === value) return;
  const caret = input.selectionStart ?? value.length;
  const removedBeforeCaret = value.slice(0, caret).length - value.slice(0, caret).replace(/[+-]/g, '').length;
  input.value = cleaned;
  const position = caret - removedBeforeCaret;
  input.setSelectionRange?.(position, position);
}

@Component({
  selector: 'app-signup',
  standalone: true,
  imports: [
    RouterLink,
    ReactiveFormsModule,
    NgxIntlTelInputModule,
    CommonModule,
    NzToolTipModule,
    TranslateModule,
    FormsModule,
  ],
  templateUrl: './signup.component.html',
  // styleUrl: './signup.component.scss',
  styleUrls: ['./signup.component.scss', '../../../../auth.scss'],

  host: { ngSkipHydration: 'true' },
})
export class SignupComponent implements OnInit, AfterViewInit {
  baseUrl = environment.baseUrl;
  SignupForm: FormGroup;
  isPasswordVisible = false;
  isLoading = signal(false);
  pagesService = inject(PagesService);
  private _router = inject(Router);
  selectedCountryData: any;
  identityRegistered = false;

  CountryISO = CountryISO;
  SearchCountryField = SearchCountryField;

  @ViewChild(NgxIntlTelInputComponent)
  phoneInputComponent!: NgxIntlTelInputComponent;

  agreeTerms: boolean = false;
  planData: any = null;
  // The fallback when IP geolocation fails — the product's home market, not Pakistan.
  selectedCountryISO: CountryISO = CountryISO.SaudiArabia;

  passwordStrength = {
    lowercase: false,
    uppercase: false,
    number: false,
    specialChar: false,
    minLength: false,
  };

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private http: HttpClient,
    private authService: AuthService,
    private route: ActivatedRoute,
    private sharedService: SharedService,
    private countryService: CountryService,
    private draft: OnboardingDraftService,
    private el: ElementRef
  ) {
    this.SignupForm = this.fb.group({
      fname: ['', [personNameValidator]],
      lname: ['', [personNameValidator]],
      email: ['', [trimmedEmailValidator]],
      phone_number: ['', [Validators.required]],
      password: ['', [Validators.required, Validators.minLength(8), Validators.pattern('^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[!@#$%^&*()_+\\-=[\\]{};\'"\\\\|,.<>/?]).{8,}$')]],
      policy: [false, Validators.requiredTrue],
      country: [''],
    });
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      const token = params['token'];
      if (token) {
        try {
          this.planData = JSON.parse(atob(token));
          this.draft.plan = this.planData;
        } catch (err) {
          console.error('Invalid token format', err);
          this.planData = this.draft.plan;
        }
      } else {
        this.planData = this.draft.plan;
      }
    });

    if (this.draft.personal) {
      this.SignupForm.patchValue(this.draft.personal, { emitEvent: false });
    }
    if (this.draft.countryISO) this.selectedCountryISO = this.draft.countryISO as CountryISO;
    const registered = this.draft.registeredIdentity;
    if (registered && this.draft.canContinue(registered.email)) {
      this.identityRegistered = true;
      ['fname', 'lname', 'email', 'phone_number'].forEach(field => this.SignupForm.get(field)?.disable());
      this.SignupForm.get('password')?.clearValidators();
      this.SignupForm.get('password')?.updateValueAndValidity();
    } else {
      this.draft.releaseRegistration();
    }
    this.SignupForm.valueChanges.subscribe(() => this.draft.savePersonal(this.SignupForm.getRawValue()));
    if (!this.draft.countryISO) this.detectUserCountry();

    // Clear Google's validatePhoneNumber error for Egypt when number is 10 digits
    this.SignupForm.get('phone_number')?.valueChanges.subscribe(() => {
      setTimeout(() => {
        const phoneControl = this.SignupForm.get('phone_number');
        if (phoneControl?.errors?.['validatePhoneNumber'] && this.isEgyptPhoneValid()) {
          const { validatePhoneNumber, ...remainingErrors } = phoneControl.errors;
          phoneControl.setErrors(Object.keys(remainingErrors).length ? remainingErrors : null);
        }
      });
    });
  }

  ngAfterViewInit(): void {
    // ngx-intl-tel-input lets "+" and "-" into the number; the dial code has its own picker, so
    // neither belongs there. Listened for on the host in the capture phase: it works whenever the
    // input renders, and pasted / autofilled text is cleaned before the form control reads it.
    const host: HTMLElement = this.el.nativeElement;
    const isPhoneInput = (target: EventTarget | null): target is HTMLInputElement =>
      target instanceof HTMLInputElement && target.classList.contains('iti__tel-input');
    host.addEventListener('keydown', (event: KeyboardEvent) => {
      if (isPhoneInput(event.target) && PHONE_BLOCKED_CHARS.test(event.key)) event.preventDefault();
    }, true);
    host.addEventListener('input', (event: Event) => {
      if (isPhoneInput(event.target)) stripPhoneSigns(event.target);
    }, true);

    setTimeout(() => {
      const phoneInput = this.el.nativeElement.querySelector('ngx-intl-tel-input input');
      if (phoneInput) {
        phoneInput.addEventListener('input', () => {
          const selectedCountry = this.phoneInputComponent?.selectedCountry;
          if (selectedCountry?.iso2 === 'eg') {
            const digits = phoneInput.value.replace(/\D/g, '');
            if (digits.length > 10) {
              phoneInput.value = digits.substring(0, 10);
              phoneInput.dispatchEvent(new Event('input', { bubbles: true }));
            }
          }
        });
      }
    }, 500);
  }

  private isEgyptPhoneValid(): boolean {
    const phoneControl = this.SignupForm.get('phone_number');
    const phoneObj = phoneControl?.value;
    if (!phoneObj) return false;

    const selectedCountry = this.phoneInputComponent?.selectedCountry;
    const isEgypt = selectedCountry?.iso2 === 'eg';
    if (!isEgypt) return false;

    const nationalNumber = (phoneObj.nationalNumber || phoneObj.number || '').replace(/[\s\-]/g, '');
    const digits = nationalNumber.replace(/\D/g, '');
    return digits.length === 10;
  }

  /** Drops leading and trailing spaces from a text field, so what is shown is what gets sent. */
  trimField(name: string): void {
    const control = this.SignupForm.get(name);
    const value = control?.value;
    if (typeof value === 'string' && value !== value.trim()) control!.setValue(value.trim());
  }

  async onSubmit() {
    if (this.isLoading()) return;
    // Enter can submit while a field still has focus, before its blur trim ran.
    TRIMMED_FIELDS.forEach(name => this.trimField(name));
    const egyptValid = this.isEgyptPhoneValid();

    if (egyptValid) {
      this.SignupForm.get('phone_number')?.setErrors(null);
    }

    if (this.SignupForm.invalid) {
      this.SignupForm.markAllAsTouched();
      this.sharedService.createNotification(
        'error',
        'Please fill in all required fields correctly.'
      );
      return;
    }

    if (this.identityRegistered) {
      const email = this.draft.registeredIdentity?.email ?? '';
      if (!this.draft.canContinue(email)) {
        this.draft.releaseRegistration();
        this.sharedService.createNotification('error', 'Your session has expired. Please log in to continue.');
        this.router.navigate(['/auth/login']);
        return;
      }
      this.isLoading.set(true);
      const token = this.createSecretToken(this.planData);
      void this.router.navigate(['/auth/company'], { queryParams: { token } })
        .finally(() => this.isLoading.set(false));
      return;
    }

    this.sharedService.showLoader();

    const selectedCountry = this.phoneInputComponent?.selectedCountry;
    if (selectedCountry?.name) {
      this.SignupForm.get('country')?.setValue(selectedCountry.name);
      this.draft.countryISO = selectedCountry.iso2;
    }

    try {
      const phoneObj = this.SignupForm.get('phone_number')?.value;

      if (!phoneObj || (!phoneObj.e164Number && !egyptValid)) {
        this.sharedService.createNotification(
          'error',
          'Valid phone number is required'
        );
        this.sharedService.hideLoader();
        return;
      }

      const finalPhone = phoneObj.e164Number || `+20${phoneObj.nationalNumber?.replace(/[\s\-]/g, '') || phoneObj.number?.replace(/[\s\-]/g, '')}`;

      const fname = String(this.SignupForm.value.fname || '').trim();
      const lname = String(this.SignupForm.value.lname || '').trim();
      const payload = {
        ...this.SignupForm.value,
        fname,
        lname,
        email: String(this.SignupForm.value.email || '').trim().toLowerCase(),
        phone_number: finalPhone,
        name: `${fname} ${lname}`,
      };

      this.draft.savePersonal({ ...this.SignupForm.value, email: payload.email });
      this.isLoading.set(true);
      this.proceedWithRegistration(payload);
    } catch (error) {
      console.error('Error processing phone number:', error);
      this.sharedService.hideLoader();
      this.sharedService.createNotification(
        'error',
        'An error occurred while processing your phone number.'
      );
    }
  }

  private proceedWithRegistration(data: any) {
    this.authService.register('SIGNUP', data).subscribe({
      next: (response: any) => {
        this.isLoading.set(false);
        this.sharedService.hideLoader();

        if (response && response.success !== false) {
          if (!response?.user?.authToken) {
            this.sharedService.createNotification('error', 'Registration session unavailable. Please log in to continue.');
            return;
          }
          this.authService.setUserToken(response);
          this.draft.markRegistered(data.email, response.user.authToken);
          const token = this.createSecretToken(this.planData);
          this.router.navigate(['auth/company'], { queryParams: { token } });
          this.sharedService.createNotification(
            'success',
            'Registration successful!'
          );
        } else {
          this.sharedService.createNotification(
            'error',
            response?.message || 'Signup failed. Please try again.'
          );
        }
      },
      error: (error: any) => {
        const backendMsg =
          error?.error?.message ||
          error?.error?.result1 ||
          error?.message ||
          '';

        const isUserAlreadyExists =
          error?.status === 409 ||
          (error?.status === 400 && (
            backendMsg.toLowerCase().includes('already exist') ||
            backendMsg.toLowerCase().includes('already registered') ||
            backendMsg.toLowerCase().includes('already in use') ||
            backendMsg.toLowerCase().includes('duplicate') ||
            error?.error?.code === 'ERROR_CODE_ALREADY_EXISTS'
          )) ||
          backendMsg.toLowerCase().includes('already exist') ||
          backendMsg.toLowerCase().includes('already registered') ||
          backendMsg.toLowerCase().includes('user with this email') ||
          backendMsg.toLowerCase().includes('account with this email') ||
          backendMsg.toLowerCase().includes('email already');

        // Direct check: If backend already returned user/company info in error payload
        const errorUser = error?.error?.data?.user ?? error?.error?.user;
        if (isUserAlreadyExists && (!errorUser?.isCompany || errorUser.isCompany.length === 0) &&
            this.authService.acceptOnboardingUser(errorUser, data.email)) {
          this.isLoading.set(false);
          this.sharedService.hideLoader();
          this.draft.markRegistered(data.email, errorUser.authToken);
          const token = this.createSecretToken(this.planData);
          this.sharedService.createNotification(
            'info',
            'Your registration is incomplete. Please complete your business profile.'
          );
          this.router.navigate(['auth/company'], { queryParams: { token } });
          return;
        }

        // If user already exists, verify if registration is incomplete by checking account
        if (isUserAlreadyExists && data?.email && data?.password) {
          this.authService.login('LOGIN', { email: data.email, password: data.password }).subscribe({
            next: (loginRes: any) => {
              this.isLoading.set(false);
              this.sharedService.hideLoader();

              const user = loginRes?.user;
              if (user?.authToken && (!user.email || user.email.trim().toLowerCase() === data.email) &&
                  (!user.isCompany || user.isCompany.length === 0)) {
                // Incomplete registration: Redirect to business profile
                this.authService.setUserToken(loginRes);
                this.authService.setToken(loginRes);
                this.draft.markRegistered(data.email, user.authToken);
                const token = this.createSecretToken(this.planData);
                this.sharedService.createNotification(
                  'info',
                  'Your registration is incomplete. Please complete your business profile.'
                );
                this.router.navigate(['auth/company'], { queryParams: { token } });
              } else {
                // Completed registration exists
                this.sharedService.createNotification(
                  'error',
                  'An account with this email already exists. Please log in.'
                );
                setTimeout(() => this.router.navigate(['/auth/login']), 1500);
              }
            },
            error: (loginErr: any) => {
              this.isLoading.set(false);
              this.sharedService.hideLoader();

              // The same person finishing a signup they abandoned: LOGIN hands back the
              // onboarding token, so continue at the step they left.
              const resumeUser = loginErr?.error?.data?.user;
              const sameUser = !!resumeUser && (!resumeUser.email || resumeUser.email.trim().toLowerCase() === data.email);
              const resumeAt = sameUser ? this.authService.resumeOnboarding(loginErr) : null;
              if (resumeAt) {
                this.draft.markRegistered(data.email, resumeUser.authToken);
                const token = this.createSecretToken(this.planData);
                this.sharedService.createNotification(
                  'info',
                  resumeAt === '/auth/company'
                    ? 'Your registration is incomplete. Please complete your business profile.'
                    : 'Your registration is incomplete. Please verify your email.'
                );
                this.router.navigate([resumeAt], { queryParams: { token } });
                return;
              }

              const loginErrMsg = loginErr?.error?.message || loginErr?.message || '';
              if (
                loginErr?.error?.company_status === "user don't have a registered company" ||
                loginErrMsg.toLowerCase().includes("haven't added a company")
              ) {
                const legacyUser = loginErr?.error?.data?.user ?? loginErr?.error?.user;
                if (this.authService.acceptOnboardingUser(legacyUser, data.email)) {
                  this.draft.markRegistered(data.email, legacyUser.authToken);
                  const token = this.createSecretToken(this.planData);
                  this.sharedService.createNotification(
                    'info',
                    'Your registration is incomplete. Please complete your business profile.'
                  );
                  this.router.navigate(['auth/company'], { queryParams: { token } });
                  return;
                }
              }

              if (backendMsg) {
                this.sharedService.createNotification('error', backendMsg);
              } else {
                this.sharedService.createNotification(
                  'error',
                  'User already exists with this email or phone number.'
                );
              }
            }
          });
          return;
        }

        this.isLoading.set(false);
        this.sharedService.hideLoader();

        if (backendMsg) {
          this.sharedService.createNotification('error', backendMsg);
        } else if (error?.status === 403) {
          this.sharedService.createNotification(
            'error',
            'Access denied. Please contact support.'
          );
        } else if (error?.status === 409) {
          this.sharedService.createNotification(
            'error',
            'User already exists with this email or phone number.'
          );
        } else {
          this.sharedService.createNotification(
            'error',
            'Signup failed. Please try again later.'
          );
        }
      },
    });
  }

  createSecretToken(plan: any): string {
    const payload = plan;
    return btoa(JSON.stringify(payload));
  }

  isRequired(controlName: string): boolean {
    const control = this.SignupForm.get(controlName);
    return control ? control.hasValidator(Validators.required) : false;
  }

  togglePasswordVisibility(): void {
    this.isPasswordVisible = !this.isPasswordVisible;
  }

  private getMissingPasswordRequirements(): string[] {
    const missing: string[] = [];
    if (!this.passwordStrength.lowercase) missing.push('lowercase letter');
    if (!this.passwordStrength.uppercase) missing.push('uppercase letter');
    if (!this.passwordStrength.number) missing.push('number');
    if (!this.passwordStrength.specialChar) missing.push('special character');
    if (!this.passwordStrength.minLength)
      missing.push('minimum length of 8 characters');
    return missing;
  }

  goToPage(routeLink: string) {
    const tree = this._router.createUrlTree([routeLink]);
    const url = this._router.serializeUrl(tree);
    const fullUrl = `${window.location.origin}#${url}`;
    window.open(fullUrl, '_blank');
  }

  private detectUserCountry() {
    this.http
      .get<any>(
        'https://api.ipgeolocation.io/ipgeo?apiKey=7394c0666cc84f05b9801f43d229666b'
      )
      .subscribe({
        next: (data) => {
          if (data && data.country_code2) {
            this.setCountryFromCode(data.country_code2);
          }
        },
        error: (err) => {
          console.warn('Could not detect country:', err);
        },
      });
  }

  private setCountryFromCode(countryCode: string) {
    const code = countryCode.toUpperCase();
    const countryKey = Object.keys(CountryISO).find(
      (key) => CountryISO[key as keyof typeof CountryISO] === code.toLowerCase()
    );

    if (countryKey) {
      this.selectedCountryISO =
        CountryISO[countryKey as keyof typeof CountryISO];

      setTimeout(() => {
        if (this.phoneInputComponent) {
          this.phoneInputComponent.selectedCountryISO = this.selectedCountryISO;
        }
      }, 300);
    }
  }
}